import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { getCurrentUser, fetchAuthSession } from 'aws-amplify/auth';
import { getFile, getFileContent, saveFileEdits, getComments, addComment, resolveComment, approveFile, rejectFile, unapproveFile, deleteFile, exportToTestRail, getUserSettings, approveRows, rejectRows, generateTestCode, addReviewers, saveUserStory, toggleCodeGen, getRepoContext, refreshRepoContext } from '../services/api';
import CsvTable from './CsvTable';
import CommentSection from './CommentSection';
import ExportDialog from './ExportDialog';
import RejectionReasonDialog from './RejectionReasonDialog';
import RefineChatPanel from './RefineChatPanel';

/** Individual comment thread in the sidebar — manages its own reply state */
function CommentSidebarThread({ thread, replies, isFocused, onNavigate, onResolve, onReply, onFocusClear }) {
  const [replyText, setReplyText] = useState('');
  const [showReply, setShowReply] = useState(false);
  const threadRef = useRef(null);

  useEffect(() => {
    if (isFocused && threadRef.current) {
      threadRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const timer = setTimeout(() => onFocusClear(), 2000);
      return () => clearTimeout(timer);
    }
  }, [isFocused, onFocusClear]);

  const handleSubmitReply = () => {
    if (!replyText.trim()) return;
    onReply(replyText.trim());
    setReplyText('');
    setShowReply(false);
  };

  return (
    <div
      ref={threadRef}
      className={`comment-sidebar-thread ${thread.resolved ? 'resolved' : ''} ${isFocused ? 'focused' : ''}`}
    >
      <div className="comment-sidebar-cell-ref" onClick={onNavigate}>
        📍 {thread.cellRef || 'General'}
        {thread.resolved && <span className="comment-resolved-badge">✓ Resolved</span>}
      </div>
      <div className="comment-sidebar-content">
        <div className="comment-sidebar-meta">
          <strong>{thread.author}</strong>
          <span>{new Date(thread.createdAt).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })} {new Date(thread.createdAt).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: '2-digit', minute: '2-digit' })}</span>
        </div>
        <div className="comment-sidebar-text">{thread.text}</div>
        <div className="comment-sidebar-actions">
          <button className="btn btn-sm btn-secondary" onClick={onResolve}>
            {thread.resolved ? 'Unresolve' : 'Resolve'}
          </button>
          {!thread.resolved && (
            <button className="btn btn-sm btn-secondary" onClick={() => setShowReply(!showReply)}>
              Reply
            </button>
          )}
        </div>
      </div>
      {replies.length > 0 && (
        <div className="comment-sidebar-replies">
          {replies.map(reply => (
            <div key={reply.commentId} className="comment-sidebar-reply">
              <div className="comment-sidebar-meta">
                <strong>{reply.author}</strong>
                <span>{new Date(reply.createdAt).toLocaleDateString('en-US', { timeZone: 'America/Chicago' })}</span>
              </div>
              <div className="comment-sidebar-text">{reply.text}</div>
            </div>
          ))}
        </div>
      )}
      {showReply && (
        <div className="comment-sidebar-reply-input">
          <input
            type="text"
            className="input"
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSubmitReply(); }}
            placeholder="Write a reply..."
            autoFocus
            style={{ fontSize: '0.8rem', padding: '6px 8px' }}
          />
          <button className="btn btn-sm btn-primary" onClick={handleSubmitReply} disabled={!replyText.trim()}>
            Send
          </button>
        </div>
      )}
    </div>
  );
}

function FileReview() {
  const { fileId } = useParams();
  const navigate = useNavigate();

  // Collapse sidebar on review page for more table space
  useEffect(() => {
    document.body.classList.add('review-page-active');
    return () => document.body.classList.remove('review-page-active');
  }, []);

  const [fileMeta, setFileMeta] = useState(null);
  const [currentUserAlias, setCurrentUserAlias] = useState('');
  const [csvData, setCsvData] = useState({ headers: [], rows: [] });
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [hasEdits, setHasEdits] = useState(false);
  const [exportResult, setExportResult] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [userSettings, setUserSettings] = useState(null);
  const [filterPriority, setFilterPriority] = useState(new Set());
  const [filterType, setFilterType] = useState(new Set());
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [highlightedRow, setHighlightedRow] = useState(null);
  const highlightTimerRef = useRef(null);
  const [generatingCode, setGeneratingCode] = useState(false);
  const [generatedFiles, setGeneratedFiles] = useState(null);
  const [refinePanelOpen, setRefinePanelOpen] = useState(false);
  const [focusedCommentCell, setFocusedCommentCell] = useState(null); // cellRef string to scroll to in sidebar
  const [showCommentSidebar, setShowCommentSidebar] = useState(true);
  const [repoContextInfo, setRepoContextInfo] = useState(null); // { analyzedAt, source }
  const [refreshingContext, setRefreshingContext] = useState(false);

  const toggleFilter = (setter, value) => {
    setter(prev => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  };

  const loadData = useCallback(async () => {
    try {
      const [meta, content, commentsData] = await Promise.all([
        getFile(fileId),
        getFileContent(fileId),
        getComments(fileId)
      ]);
      setFileMeta(meta);
      setComments(commentsData.comments || []);

      // Parse CSV content
      const parsed = Papa.parse(content.csvText, { header: true, skipEmptyLines: true });
      setCsvData({ headers: parsed.meta.fields || [], rows: parsed.data });
    } catch (err) {
      setError(`Failed to load file: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fileId]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    getUserSettings()
      .then(data => setUserSettings(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload?.email || '';
        const user = await getCurrentUser();
        const raw = email || user.username || '';
        setCurrentUserAlias(raw.includes('@') ? raw.split('@')[0] : raw);
      } catch { /* not signed in */ }
    })();
  }, []);

  const handleCellEdit = (rowIndex, column, value) => {
    const updatedRows = [...csvData.rows];
    updatedRows[rowIndex] = { ...updatedRows[rowIndex], [column]: value };
    setCsvData({ ...csvData, rows: updatedRows });
    setHasEdits(true);
  };

  const handleAddRow = () => {
    const emptyRow = {};
    csvData.headers.forEach(h => { emptyRow[h] = ''; });
    setCsvData({ ...csvData, rows: [...csvData.rows, emptyRow] });
    setHasEdits(true);
  };

  const handleDeleteRow = (rowIndex) => {
    const updatedRows = csvData.rows.filter((_, i) => i !== rowIndex);
    setCsvData({ ...csvData, rows: updatedRows });
    setHasEdits(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const csvText = Papa.unparse(csvData.rows, { columns: csvData.headers });
      await saveFileEdits(fileId, csvText);
      setHasEdits(false);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleAddComment = async (commentText, cellRef, parentCommentId) => {
    try {
      const newComment = await addComment(fileId, { text: commentText, cellRef, parentCommentId: parentCommentId || null });
      setComments([...comments, newComment]);
    } catch (err) {
      setError(`Failed to add comment: ${err.message}`);
    }
  };

  const handleResolveComment = async (commentId, resolved) => {
    try {
      await resolveComment(fileId, commentId, resolved);
      setComments(comments.map(c =>
        c.commentId === commentId ? { ...c, resolved, resolvedBy: resolved ? currentUserAlias : null, resolvedAt: resolved ? new Date().toISOString() : null } : c
      ));
    } catch (err) {
      setError(`Failed to ${resolved ? 'resolve' : 'unresolve'} thread: ${err.message}`);
    }
  };

  const handleNavigateToRow = (rowIndex, cellRef) => {
    // Clear any active filters so the row is visible
    setFilterPriority(new Set());
    setFilterType(new Set());
    // Small delay to let tab switch render, then scroll and highlight
    setTimeout(() => {
      setHighlightedRow(rowIndex);
      const rowEl = document.querySelector(`[data-row-index="${rowIndex}"]`);
      if (rowEl) {
        rowEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      // Clear highlight after 3 seconds
      if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
      highlightTimerRef.current = setTimeout(() => setHighlightedRow(null), 3000);
    }, 100);
  };

  const getTestCasesFromRows = (rowIndices) => {
    const rows = rowIndices ? Array.from(rowIndices).map(i => csvData.rows[i]).filter(Boolean) : csvData.rows;
    return rows.map(row => {
      const tc = {};
      csvData.headers.forEach(h => { tc[h] = row[h] || ''; });
      return tc;
    });
  };

  const handleGenerateTestCode = async (rowIndices) => {
    const testCases = getTestCasesFromRows(rowIndices);
    if (testCases.length === 0) return;
    setGeneratingCode(true);
    setGeneratedFiles(null);
    setError('');
    try {
      const result = await generateTestCode(testCases);
      setGeneratedFiles(result);
    } catch (err) {
      setError(`Code generation failed: ${err.message}`);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleDownloadCode = async () => {
    if (!generatedFiles?.files) return;

    // Try File System Access API (Chrome/Edge) for directory picking
    if (window.showDirectoryPicker) {
      try {
        const dirHandle = await window.showDirectoryPicker({ mode: 'readwrite' });
        for (const file of generatedFiles.files) {
          // Create subdirectories if needed (e.g., "src/com/amazon/.../TestFile.java")
          const parts = file.fileName.split('/');
          let currentDir = dirHandle;
          for (let i = 0; i < parts.length - 1; i++) {
            currentDir = await currentDir.getDirectoryHandle(parts[i], { create: true });
          }
          const fileHandle = await currentDir.getFileHandle(parts[parts.length - 1], { create: true });
          const writable = await fileHandle.createWritable();
          await writable.write(file.content);
          await writable.close();
        }
        alert(`Saved ${generatedFiles.files.length} file${generatedFiles.files.length !== 1 ? 's' : ''} to the selected directory.`);
        return;
      } catch (err) {
        if (err.name === 'AbortError') return; // user cancelled
        console.warn('Directory picker failed, falling back to individual saves:', err);
      }
    }

    // Fallback: showSaveFilePicker per file (Chrome/Edge)
    if (window.showSaveFilePicker) {
      for (const file of generatedFiles.files) {
        try {
          const name = file.fileName.split('/').pop();
          const handle = await window.showSaveFilePicker({
            suggestedName: name,
            types: [{ description: 'Java file', accept: { 'text/x-java': ['.java'] } }]
          });
          const writable = await handle.createWritable();
          await writable.write(file.content);
          await writable.close();
        } catch (err) {
          if (err.name === 'AbortError') break; // user cancelled
        }
      }
      return;
    }

    // Final fallback: regular download
    for (const file of generatedFiles.files) {
      const name = file.fileName.split('/').pop();
      const blob = new Blob([file.content], { type: 'text/x-java' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    }
  };

  const handleCopyAgentPrompt = async (rowIndices) => {
    const testCases = getTestCasesFromRows(rowIndices);
    if (testCases.length === 0) return;
    setGeneratingCode(true);
    setError('');
    try {
      const result = await generateTestCode(testCases, 'agent-prompt');
      const fullText = `${result.agentPrompt}\n\n---\nRun: ${result.agentCommand}`;
      await navigator.clipboard.writeText(fullText);
      setError('');
      alert(`Copied agent prompt for ${result.testCaseCount} test case${result.testCaseCount !== 1 ? 's' : ''}. Run:\n\n${result.agentCommand}\n\nThen paste the prompt.`);
    } catch (err) {
      setError(`Failed to generate agent prompt: ${err.message}`);
    } finally {
      setGeneratingCode(false);
    }
  };

  const handleApprove = async () => {
    try {
      await approveFile(fileId);
      await loadData();
    } catch (err) {
      setError(`Approval failed: ${err.message}`);
    }
  };

  const handleReject = async () => {
    const reason = prompt('Please provide a reason for rejection:');
    if (!reason) return;
    try {
      await rejectFile(fileId, reason);
      await loadData();
    } catch (err) {
      setError(`Rejection failed: ${err.message}`);
    }
  };

  const handleUnapprove = async () => {
    try {
      await unapproveFile(fileId);
      await loadData();
    } catch (err) {
      setError(`Remove approval failed: ${err.message}`);
    }
  };

  const handleApproveRows = async () => {
    if (selectedRows.size === 0) return;
    try {
      await approveRows(fileId, Array.from(selectedRows));
      setSelectedRows(new Set());
      await loadData();
    } catch (err) {
      setError(`Row approval failed: ${err.message}`);
    }
  };

  const handleRejectRows = async (reason) => {
    if (selectedRows.size === 0) return;
    try {
      await rejectRows(fileId, Array.from(selectedRows), reason);
      setSelectedRows(new Set());
      setRejectionDialogOpen(false);
      await loadData();
    } catch (err) {
      setError(`Row rejection failed: ${err.message}`);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete "${fileMeta?.fileName}"? This cannot be undone.`)) return;
    try {
      await deleteFile(fileId);
      navigate('/');
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleToggleCodeGen = async () => {
    const newEnabled = !fileMeta?.codeGenEnabled;
    try {
      await toggleCodeGen(fileId, newEnabled);
      setFileMeta(prev => ({ ...prev, codeGenEnabled: newEnabled }));
    } catch (err) {
      setError(`Failed to toggle code generation: ${err.message}`);
    }
  };

  // Fetch repo context info when code gen is enabled
  useEffect(() => {
    if (fileMeta?.codeGenEnabled && !repoContextInfo) {
      getRepoContext()
        .then(data => setRepoContextInfo({ analyzedAt: data.analyzedAt, source: data.source }))
        .catch(() => setRepoContextInfo({ analyzedAt: null, source: 'unavailable' }));
    }
  }, [fileMeta?.codeGenEnabled, repoContextInfo]);

  const handleRefreshRepoContext = async () => {
    setRefreshingContext(true);
    try {
      const result = await refreshRepoContext();
      setRepoContextInfo({ analyzedAt: result.repoContext?.analyzedAt, source: 's3' });
    } catch (err) {
      setError(`Repo context refresh failed: ${err.message}`);
    } finally {
      setRefreshingContext(false);
    }
  };

  const handleAddReviewers = async () => {
    const input = prompt('Enter reviewer alias(es), comma-separated:');
    if (!input || !input.trim()) return;
    const newApprovers = input.split(',').map(a => a.trim()).filter(a => a.length > 0);
    if (newApprovers.length === 0) return;
    try {
      await addReviewers(fileId, newApprovers);
      await loadData();
    } catch (err) {
      setError(`Failed to add reviewers: ${err.message}`);
    }
  };

  const handleExportToTestRail = async (payload) => {
    setExportResult(null);
    setError('');
    try {
      const result = await exportToTestRail(fileId, payload);
      setExportResult(result);
      await loadData();
    } catch (err) {
      setError(`Export failed: ${err.message}`);
    }
  };

  // Memoized filtered rows — avoids re-creating array reference on every render
  const filteredRows = useMemo(() =>
    csvData.rows
      .map((r, i) => ({ ...r, _trueOriginalIndex: i }))
      .filter(r => {
        if (filterPriority.size > 0 && !filterPriority.has((r.Priority || 'Unset').trim() || 'Unset')) return false;
        if (filterType.size > 0 && !filterType.has((r.Type || 'Unset').trim() || 'Unset')) return false;
        return true;
      }),
    [csvData.rows, filterPriority, filterType]
  );

  // Memoized visible row indices for comment sidebar filtering
  const visibleRowIndices = useMemo(() => {
    return new Set(csvData.rows.map((r, i) => {
      if (filterPriority.size > 0 && !filterPriority.has((r.Priority || 'Unset').trim() || 'Unset')) return -1;
      if (filterType.size > 0 && !filterType.has((r.Type || 'Unset').trim() || 'Unset')) return -1;
      return i;
    }).filter(i => i >= 0));
  }, [csvData.rows, filterPriority, filterType]);

  if (loading) return <div className="loading">Loading review...</div>;
  if (error && !fileMeta) return <div className="error-message">{error}</div>;

  return (
    <div className="review-container">
      <div className="review-header">
        <div className="review-title">
          <h2>📄 {fileMeta?.fileName}</h2>
          <span className={`badge badge-${fileMeta?.status === 'Approved' ? 'success' : fileMeta?.status === 'Rejected' ? 'danger' : 'warning'}`}>
            {fileMeta?.status}
          </span>
        </div>

        <div className="review-meta">
          <span>Uploaded by: {fileMeta?.uploadedBy}</span>
          <span>Rows: {csvData.rows.length}</span>
          <span>Columns: {csvData.headers.length}</span>
        </div>

        <div className="approval-tracker">
          <h4>Approvals</h4>
          <div className="approver-list">
            {fileMeta?.approvers?.map((approver, i) => (
              <div key={i} className={`approver-chip ${approver.approved ? 'approved' : 'pending'}`}>
                <span className="approver-icon">{approver.approved ? '✅' : '⏳'}</span>
                <span>{approver.alias}</span>
              </div>
            ))}
            {fileMeta?.status !== 'Approved' && currentUserAlias && fileMeta?.uploadedBy?.toLowerCase() === currentUserAlias.toLowerCase() && (
              <button onClick={handleAddReviewers} className="approver-chip add-approver" title="Add new reviewer(s)">
                <span className="approver-icon">➕</span>
                <span>Add Reviewer</span>
              </button>
            )}
          </div>
        </div>

        <div className="review-actions">
          {hasEdits && (
            <button onClick={handleSave} className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Edits'}
            </button>
          )}
          {fileMeta?.status !== 'Approved' && fileMeta?.status !== 'Rejected' && (
            <>
              <button onClick={handleApprove} className="btn btn-success">Full File Approval</button>
              <button onClick={handleReject} className="btn btn-danger">Reject Entire File</button>
            </>
          )}
          {selectedRows.size > 0 && fileMeta?.status !== 'Approved' && fileMeta?.status !== 'Rejected' && (
            <>
              <button onClick={handleApproveRows} className="btn btn-success">Approve Selected ({selectedRows.size})</button>
              <button onClick={() => setRejectionDialogOpen(true)} className="btn btn-danger">Reject Selected ({selectedRows.size})</button>
            </>
          )}
          {fileMeta?.approvers?.some(a => a.approved) && fileMeta?.status !== 'Rejected' && (
            <button onClick={handleUnapprove} className="btn btn-secondary">Remove My Approval</button>
          )}
          {fileMeta?.status !== 'Approved' && (
            <button onClick={handleDelete} className="btn btn-danger">Delete File</button>
          )}
          {(fileMeta?.status === 'Approved' || fileMeta?.status === 'Partially Approved') && currentUserAlias && fileMeta?.uploadedBy?.toLowerCase() === currentUserAlias.toLowerCase() && (
            <button onClick={() => setExportDialogOpen(true)} className="btn btn-primary">
              {fileMeta?.exportedToTestRail ? '🔄 Re-import to TestRail' : '📤 Import to TestRail'}
            </button>
          )}
          {csvData.rows.length > 0 && fileMeta?.codeGenEnabled && (
            <>
              <button onClick={() => handleGenerateTestCode()} className="btn btn-secondary" disabled={generatingCode}>
                {generatingCode ? '⏳ Generating...' : '🧬 Generate All'}
              </button>
              <button onClick={() => handleCopyAgentPrompt()} className="btn btn-secondary" disabled={generatingCode}>
                🤖 Copy All for Agent
              </button>
            </>
          )}
        </div>
      </div>

      {exportResult && (
        <div className="export-result" style={{ background: '#e6f2ee', border: '1px solid #00674b', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 16, fontSize: '0.875rem' }}>
          <strong>{exportResult.message}</strong>
          {exportResult.results?.some(r => r.status === 'failed') && (
            <ul style={{ margin: '8px 0 0 16px' }}>
              {exportResult.results.filter(r => r.status === 'failed').map((r, i) => (
                <li key={i} style={{ color: '#c0392b' }}>{r.title}: {r.error}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {fileMeta?.exportedToTestRail && !exportResult && (
        <div style={{ background: 'var(--accent-light)', borderRadius: 'var(--radius)', padding: '8px 14px', marginBottom: 16, fontSize: '0.85rem', color: '#6b4f10' }}>
          ✅ Previously imported to TestRail{fileMeta.exportedAt ? ` on ${new Date(fileMeta.exportedAt).toLocaleString('en-US', { timeZone: 'America/Chicago' })}` : ''}
        </div>
      )}

      {fileMeta?.status === 'Rejected' && (
        <div style={{ background: '#fde8e8', border: '1px solid var(--danger, #c0392b)', borderRadius: 'var(--radius)', padding: '12px 16px', marginBottom: 16, fontSize: '0.875rem' }}>
          <strong>❌ Rejected{fileMeta.rejectedBy ? ` by ${fileMeta.rejectedBy}` : ''}</strong>
          {fileMeta.rejectionReason && (
            <p style={{ margin: '6px 0 0', color: 'var(--text-primary, #333)' }}>Reason: {fileMeta.rejectionReason}</p>
          )}
        </div>
      )}

      {error && <div className="error-message" role="alert">{error}</div>}

      {generatedFiles && generatedFiles.files && (
        <div className="codegen-dialog-overlay" onClick={() => setGeneratedFiles(null)}>
          <div className="codegen-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="codegen-dialog-header">
              <span>🧬 Generated Code — {generatedFiles.language} ({generatedFiles.files.length} file{generatedFiles.files.length !== 1 ? 's' : ''})</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-primary btn-sm" onClick={handleDownloadCode}>📂 Save to Directory</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setGeneratedFiles(null)}>✕ Close</button>
              </div>
            </div>
            <div className="codegen-dialog-body">
              {(() => {
                const frameworkFiles = generatedFiles.files.filter(f => f.fileName.startsWith('HawkeyeAutomationFramework'));
                const scriptFiles = generatedFiles.files.filter(f => f.fileName.startsWith('HawkeyeAutomationScripts'));
                const otherFiles = generatedFiles.files.filter(f => !f.fileName.startsWith('HawkeyeAutomationFramework') && !f.fileName.startsWith('HawkeyeAutomationScripts'));
                return (
                  <>
                    {frameworkFiles.length > 0 && (
                      <div className="generated-repo-group">
                        <div className="generated-repo-label">📦 HawkeyeAutomationFramework — Library ({frameworkFiles.length})</div>
                        {frameworkFiles.map((file, i) => (
                          <div key={`fw-${i}`} className="generated-file">
                            <div className="generated-file-name">
                              {file.fileName.endsWith('.json') ? '⚙️' : '📄'} {file.fileName.replace('HawkeyeAutomationFramework/', '')}
                            </div>
                            <pre className="generated-file-content"><code>{file.content}</code></pre>
                          </div>
                        ))}
                      </div>
                    )}
                    {scriptFiles.length > 0 && (
                      <div className="generated-repo-group">
                        <div className="generated-repo-label">📦 HawkeyeAutomationScripts — Tests ({scriptFiles.length})</div>
                        {scriptFiles.map((file, i) => (
                          <div key={`sc-${i}`} className="generated-file">
                            <div className="generated-file-name">📄 {file.fileName.replace('HawkeyeAutomationScripts/', '')}</div>
                            <pre className="generated-file-content"><code>{file.content}</code></pre>
                          </div>
                        ))}
                      </div>
                    )}
                    {otherFiles.length > 0 && otherFiles.map((file, i) => (
                      <div key={`ot-${i}`} className="generated-file">
                        <div className="generated-file-name">📄 {file.fileName}</div>
                        <pre className="generated-file-content"><code>{file.content}</code></pre>
                      </div>
                    ))}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      <div className="review-layout">
        {/* Main content — always table view */}
        <div className="review-main">
            <div className="tc-stats-bar">
              <div className="tc-stats-group">
                <span className="tc-stats-label">By Priority:</span>
                {Object.entries(
                  csvData.rows.reduce((acc, r) => {
                    const p = (r.Priority || 'Unset').trim() || 'Unset';
                    acc[p] = (acc[p] || 0) + 1;
                    return acc;
                  }, {})
                ).sort((a, b) => a[0].localeCompare(b[0])).map(([k, v]) => (
                  <span
                    key={k}
                    className={`tc-stats-chip clickable${filterPriority.has(k) ? ' active' : ''}`}
                    onClick={() => toggleFilter(setFilterPriority, k)}
                  >{k} <strong>{v}</strong></span>
                ))}
              </div>
              <div className="tc-stats-group">
                <span className="tc-stats-label">By Type:</span>
                {Object.entries(
                  csvData.rows.reduce((acc, r) => {
                    const t = (r.Type || 'Unset').trim() || 'Unset';
                    acc[t] = (acc[t] || 0) + 1;
                    return acc;
                  }, {})
                ).sort((a, b) => a[0].localeCompare(b[0])).map(([k, v]) => (
                  <span
                    key={k}
                    className={`tc-stats-chip clickable${filterType.has(k) ? ' active' : ''}`}
                    onClick={() => toggleFilter(setFilterType, k)}
                  >{k} <strong>{v}</strong></span>
                ))}
              </div>
              {(filterPriority.size > 0 || filterType.size > 0) && (
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => { setFilterPriority(new Set()); setFilterType(new Set()); }}
                  style={{ marginLeft: 'auto' }}
                >✕ Clear filters</button>
              )}
              <button
                className={`btn btn-sm ${showCommentSidebar ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setShowCommentSidebar(!showCommentSidebar)}
                style={{ marginLeft: filterPriority.size > 0 || filterType.size > 0 ? 8 : 'auto' }}
              >
                💬 Comments
              </button>
            </div>
            {csvData.rows.length > 0 && (
              <div className="code-gen-toolbar">
                <div className="code-gen-actions">
                  {/* Code Gen Toggle — only visible to file owner */}
                  {currentUserAlias && fileMeta?.uploadedBy?.toLowerCase() === currentUserAlias.toLowerCase() && (
                    <label className="codegen-toggle" title="Enable or disable code generation for this file">
                      <input
                        type="checkbox"
                        checked={!!fileMeta?.codeGenEnabled}
                        onChange={handleToggleCodeGen}
                      />
                      <span className="codegen-toggle-label">Code Gen</span>
                    </label>
                  )}
                  {/* Show non-owner indicator if code gen is enabled/disabled */}
                  {currentUserAlias && fileMeta?.uploadedBy?.toLowerCase() !== currentUserAlias.toLowerCase() && (
                    <span className={`codegen-status-badge ${fileMeta?.codeGenEnabled ? 'enabled' : 'disabled'}`}>
                      {fileMeta?.codeGenEnabled ? '🧬 Code Gen Enabled' : '🧬 Code Gen Disabled'}
                    </span>
                  )}
                  {/* Code gen action buttons — only shown when enabled */}
                  {fileMeta?.codeGenEnabled && (
                    <>
                      <span className="code-gen-divider">|</span>
                      <button onClick={() => handleGenerateTestCode()} className="btn btn-sm btn-secondary" disabled={generatingCode}>
                        {generatingCode ? '⏳ Generating...' : '🧬 Generate All'}
                      </button>
                      <button onClick={() => handleCopyAgentPrompt()} className="btn btn-sm btn-secondary" disabled={generatingCode}>
                        🤖 Copy All for Agent
                      </button>
                      {selectedRows.size > 0 && (
                        <>
                          <span className="code-gen-divider">|</span>
                          <button onClick={() => handleGenerateTestCode(selectedRows)} className="btn btn-sm btn-primary" disabled={generatingCode}>
                            🧬 Generate Selected ({selectedRows.size})
                          </button>
                          <button onClick={() => handleCopyAgentPrompt(selectedRows)} className="btn btn-sm btn-primary" disabled={generatingCode}>
                            🤖 Copy Selected for Agent ({selectedRows.size})
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
                {fileMeta?.codeGenEnabled && selectedRows.size === 0 && <span className="code-gen-hint">Select rows to generate code for specific test cases, or use per-row 🧬 button</span>}
                {fileMeta?.codeGenEnabled && repoContextInfo && (
                  <span className="code-gen-context-info">
                    {repoContextInfo.source === 's3' && repoContextInfo.analyzedAt
                      ? `Context synced: ${new Date(repoContextInfo.analyzedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}`
                      : repoContextInfo.source === 'static' ? 'Using static context' : 'Context unavailable'}
                    {currentUserAlias && fileMeta?.uploadedBy?.toLowerCase() === currentUserAlias.toLowerCase() && (
                      <button
                        className="btn-refresh-context"
                        onClick={handleRefreshRepoContext}
                        disabled={refreshingContext}
                        title="Re-analyze repos now (normally runs nightly at midnight UTC)"
                      >
                        {refreshingContext ? '⏳' : '🔄'}
                      </button>
                    )}
                  </span>
                )}
                {!fileMeta?.codeGenEnabled && currentUserAlias && fileMeta?.uploadedBy?.toLowerCase() === currentUserAlias.toLowerCase() && (
                  <span className="code-gen-hint">Enable the toggle to show code generation actions</span>
                )}
              </div>
            )}
            <CsvTable
            headers={csvData.headers}
            rows={filteredRows}
            onCellEdit={handleCellEdit}
            onAddRow={handleAddRow}
            onDeleteRow={handleDeleteRow}
            editable={fileMeta?.status !== 'Approved'}
            comments={comments}
            onAddComment={handleAddComment}
            onResolve={handleResolveComment}
            selectable={true}
            selectedRows={selectedRows}
            onSelectionChange={setSelectedRows}
            rowReviews={fileMeta?.rowReviews || {}}
            currentUserAlias={currentUserAlias}
            highlightedRow={highlightedRow}
            codeGenEnabled={!!fileMeta?.codeGenEnabled}
            onRowCodeGen={(rowIndex) => handleGenerateTestCode(new Set([rowIndex]))}
            onRowCopyAgent={(rowIndex) => handleCopyAgentPrompt(new Set([rowIndex]))}
            generatingCode={generatingCode}
            onCommentIconClick={(cellRef) => {
              if (!showCommentSidebar) setShowCommentSidebar(true);
              setFocusedCommentCell(cellRef);
            }}
          />
          {/* Refine with AI — opens docked panel */}
          {csvData.rows.length > 0 && (
            <div style={{ marginTop: 16, padding: '14px 16px', background: 'var(--card-bg, #fff)', border: '1px solid var(--border, #e0e0e0)', borderRadius: 'var(--radius, 8px)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                ✨ Refine with AI
              </div>
              <button
                className="btn btn-primary"
                onClick={() => setRefinePanelOpen(true)}
                style={{ fontSize: '0.85rem' }}
              >
                ✨ Refine with AI
              </button>
            </div>
          )}
        </div>

        {/* Right-side comment panel (Word-style) — only shows comments for visible rows */}
        {showCommentSidebar && (
        <div className="review-comment-sidebar">
          <div className="comment-sidebar-header">
            <h4 style={{ margin: 0, fontSize: '0.9rem' }}>💬 Comments</h4>
            <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>
              {(() => {
                const visibleThreads = comments.filter(c => !c.parentCommentId && c.cellRef).filter(c => {
                  const match = c.cellRef.match(/R(\d+):/);
                  return match && visibleRowIndices.has(parseInt(match[1]) - 1);
                });
                const openCount = visibleThreads.filter(c => !c.resolved).length;
                return `${visibleThreads.length} thread${visibleThreads.length !== 1 ? 's' : ''}${openCount < visibleThreads.length ? ` · ${openCount} open` : ''}`;
              })()}
            </span>
          </div>
          <div className="comment-sidebar-body">
            {(() => {
              const visibleThreads = comments
                .filter(c => !c.parentCommentId && c.cellRef)
                .filter(c => {
                  const match = c.cellRef.match(/R(\d+):/);
                  return match && visibleRowIndices.has(parseInt(match[1]) - 1);
                })
                .sort((a, b) => {
                  const rowA = parseInt((a.cellRef || 'R0').match(/R(\d+)/)?.[1] || '0');
                  const rowB = parseInt((b.cellRef || 'R0').match(/R(\d+)/)?.[1] || '0');
                  return rowA - rowB;
                });

              if (visibleThreads.length === 0) {
                return (
                  <div style={{ padding: '24px 16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.83rem' }}>
                    No comments for the current view. Right-click a cell to add one.
                  </div>
                );
              }

              return visibleThreads.map(thread => {
                const replies = comments.filter(c => c.parentCommentId === thread.commentId).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
                const isFocused = focusedCommentCell && thread.cellRef === focusedCommentCell;
                return (
                  <CommentSidebarThread
                    key={thread.commentId}
                    thread={thread}
                    replies={replies}
                    isFocused={isFocused}
                    onNavigate={() => {
                      if (thread.cellRef) {
                        const match = thread.cellRef.match(/R(\d+):(.+)/);
                        if (match) handleNavigateToRow(parseInt(match[1]) - 1, thread.cellRef);
                      }
                    }}
                    onResolve={() => handleResolveComment(thread.commentId, !thread.resolved)}
                    onReply={(text) => handleAddComment(text, null, thread.commentId)}
                    onFocusClear={() => setFocusedCommentCell(null)}
                  />
                );
              });
            })()}
          </div>
        </div>
        )}
      </div>

      <RefineChatPanel
        isOpen={refinePanelOpen}
        onClose={() => setRefinePanelOpen(false)}
        testCases={csvData.rows.map(row => {
          const tc = {};
          csvData.headers.forEach(h => { tc[h] = row[h] || ''; });
          return tc;
        })}
        onUpdate={(refined) => {
          if (refined.length === 0) return;
          const newHeaders = Object.keys(refined[0]).filter(k => k !== '_source');
          const newRows = refined.map(tc => {
            const row = {};
            newHeaders.forEach(h => { row[h] = tc[h] || ''; });
            return row;
          });
          setCsvData({ headers: newHeaders, rows: newRows });
          setHasEdits(true);
        }}
        userStory={fileMeta?.userStory || ''}
        onUserStoryChange={async (story) => {
          try {
            await saveUserStory(fileId, story);
            setFileMeta(prev => ({ ...prev, userStory: story }));
          } catch (err) {
            setError(`Failed to save user story: ${err.message}`);
          }
        }}
      />

      <ExportDialog
        isOpen={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
        csvHeaders={csvData.headers}
        csvRows={csvData.rows}
        onExport={handleExportToTestRail}
        defaultProjectId={userSettings?.defaultProjectId || null}
        defaultSectionId={userSettings?.defaultSectionId || null}
        defaultSuiteId={userSettings?.defaultSuiteId || null}
        rowReviews={fileMeta?.rowReviews || {}}
        rowExports={fileMeta?.rowExports || {}}
        fileStatus={fileMeta?.status || ''}
      />

      <RejectionReasonDialog
        isOpen={rejectionDialogOpen}
        onClose={() => setRejectionDialogOpen(false)}
        onSubmit={handleRejectRows}
        rowCount={selectedRows.size}
      />
    </div>
  );
}

export default FileReview;
